import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export function formatDate(dateString) {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
}

export function formatDateTime(dateString) {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

export function formatCurrency(amount) {
  if (amount === null || amount === undefined || Number.isNaN(Number(amount))) return '-';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(Number(amount));
}

export function formatPhone(phone) {
  if (!phone) return '-';
  return phone.replace(/(\d{5})(\d{5})/, '$1 $2');
}

export function maskPAN(pan) {
  if (!pan) return '-';
  return pan.substring(0, 2) + '****' + pan.substring(6);
}

export function maskAadhaar(aadhaar) {
  if (!aadhaar) return '-';
  return '****-****-' + aadhaar.substring(8);
}

export function getInitials(name) {
  if (!name) return '?';
  return name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .substring(0, 2);
}

export function getStatusBadgeClass(status) {
  const statusMap = {
    'Active': 'badge-active',
    'Inactive': 'badge-inactive',
    'Expired': 'badge-pending',
    'Deceased': 'badge-overdue',
    'New': 'badge-new',
    'Contacted': 'badge-contacted',
    'Follow-Up': 'badge-pending',
    'Qualified': 'badge-qualified',
    'Converted': 'badge-converted',
    'Closed': 'badge-closed',
    'Pending': 'badge-pending',
    'In Progress': 'badge-contacted',
    'Completed': 'badge-completed',
    'Overdue': 'badge-overdue',
    'Cancelled': 'badge-closed',
    'Rescheduled': 'badge-pending',
    // New lead statuses
    "Didn't Pick Up": 'badge-overdue',
    'Busy / Call After Sometime': 'badge-pending',
    'Follow Up - Brochure Shared': 'badge-contacted',
    'Interested for Meeting': 'badge-qualified',
    'Meeting Scheduled': 'badge-active',
    'Meeting Done - Follow Up': 'badge-converted',
    // Task statuses
    'Under Process': 'badge-contacted',
    'Rejection': 'badge-overdue'
  };
  return statusMap[status] || 'badge-inactive';
}

export function getPriorityBadgeClass(priority) {
  const priorityMap = {
    'High': 'badge-high',
    'Medium': 'badge-medium',
    'Low': 'badge-low'
  };
  return priorityMap[priority] || 'badge-inactive';
}

export function isMinor(dateOfBirth) {
  if (!dateOfBirth) return false;
  const dob = new Date(dateOfBirth);
  const today = new Date();
  const age = today.getFullYear() - dob.getFullYear();
  const monthDiff = today.getMonth() - dob.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
    return age - 1 < 18;
  }
  return age < 18;
}

const ENTITY_TYPES = ['HUF', 'Firm', 'Company', 'Trust'];

export function getClientDisplayName(client) {
  if (!client) return '';
  if (ENTITY_TYPES.includes(client.client_type) && client.entity_name) {
    return client.entity_name;
  }
  return client.full_name || '';
}


export function isOverdue(dueDate) {
  if (!dueDate) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(dueDate);
  due.setHours(0, 0, 0, 0);
  return due < today;
}

export function isToday(dateString) {
  if (!dateString) return false;
  const today = new Date();
  const date = new Date(dateString);
  return today.toDateString() === date.toDateString();
}

export function formatApiError(error) {
  const detail = error?.response?.data?.detail;
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map(e => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
  }
  // Structured duplicate-lead payload: { message, matched_on, existing_lead: {...} }
  if (detail && typeof detail.message === 'string') return detail.message;
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

/**
 * If the error is a 409 duplicate-lead response, returns the existing lead payload;
 * otherwise null. Used by Lead form/edit pages to render a "View existing lead" link.
 */
export function getDuplicateLead(error) {
  if (error?.response?.status !== 409) return null;
  const detail = error?.response?.data?.detail;
  if (detail && typeof detail === 'object' && detail.existing_lead) return detail.existing_lead;
  return null;
}

export function validatePAN(pan) {
  const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
  return panRegex.test(pan?.toUpperCase());
}

export function validatePhone(phone) {
  const phoneRegex = /^[6-9]\d{9}$/;
  return phoneRegex.test(phone);
}

export function validateEmail(email) {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

export function validatePinCode(pinCode) {
  const pinRegex = /^[1-9][0-9]{5}$/;
  return pinRegex.test(pinCode);
}
